import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Lock, Send, Bot, Loader2, ArrowRight } from "lucide-react";

export const visitorRef = (params) => params.get("ref") || localStorage.getItem("sam_ref") || "";

const TIER_NAMES = { free: "Free", listing: "Listing", growth: "Growth", pro: "Pro", max: "Max" };
const LOCKED = [["phone", "Phone number + call tracking", "Growth"], ["links", "Website & social links", "Growth"],
  ["photos", "Photo gallery", "Growth"], ["products", "Products & services", "Growth"], ["ai_chat", "24/7 AI assistant chat", "Pro"]];

export function UpgradeNudge({ b }) {
  const missing = LOCKED.filter(([k]) => !b.features?.[k]);
  if (!missing.length) return null;
  return (
    <div className="rounded-xl border-2 border-sam-amber/60 bg-amber-50 p-4" data-testid="listing-upgrade-nudge">
      <div className="text-xs font-bold uppercase tracking-wide text-sam-crimson">Only you see this · your plan: {TIER_NAMES[b.tier] || b.tier}</div>
      <p className="mt-1 text-sm text-sam-navy font-semibold">{b.features?.phone ? "Upgrade to unlock more on your listing:" : "Customers can only reach you through the contact form right now. Upgrade to show more:"}</p>
      <ul className="mt-2 grid sm:grid-cols-2 gap-1 text-sm text-sam-slate">
        {missing.map(([k, label, tier]) => <li key={k} className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-sam-amber" />{label} <span className="text-[11px] text-sam-slate/70">({tier}+)</span></li>)}
      </ul>
      <Button asChild size="sm" className="mt-3 bg-sam-crimson hover:bg-sam-crimson/90" data-testid="listing-upgrade-btn"><Link to="/plans">See plans & upgrade <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
    </div>
  );
}

export function ContactForm({ b, refCode }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const send = async () => {
    if (!f.name.trim()) return toast.error("Please add your name");
    setBusy(true);
    try { await api.post(`/businesses/${b.id}/contact`, { ...f, ref: refCode }); setSent(true); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    setBusy(false);
  };
  if (sent) return <div className="rounded-xl border border-sam-green/40 bg-green-50 p-4 text-sm text-sam-navy" data-testid="listing-contact-sent">Thanks! {b.name} has your message and will reach out soon.</div>;
  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-2" data-testid="listing-contact-form">
      <div className="font-display font-bold text-sam-navy">Contact {b.name}</div>
      <p className="text-xs text-sam-slate">Sent through Saving America Money.</p>
      <Input placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="listing-contact-name" />
      <Input placeholder="Phone" type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="listing-contact-phone" />
      <Input placeholder="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="listing-contact-email" />
      <Textarea placeholder="How can they help?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className="min-h-[70px]" data-testid="listing-contact-message" />
      <Button className="w-full bg-sam-crimson hover:bg-sam-crimson/90" onClick={send} disabled={busy} data-testid="listing-contact-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}Send message</Button>
    </div>
  );
}

export function BizChat({ b }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sid, setSid] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const m = text.trim();
    if (!m) return;
    setMsgs((x) => [...x, { role: "user", text: m }]); setText(""); setBusy(true);
    try {
      const { data } = await api.post(`/businesses/${b.id}/ai-chat`, { message: m, session_id: sid });
      setSid(data.session_id); setMsgs((x) => [...x, { role: "assistant", text: data.reply }]);
    } catch { setMsgs((x) => [...x, { role: "assistant", text: "Sorry, I couldn't answer right now." }]); }
    setBusy(false);
  };
  if (!open) return <Button className="w-full bg-sam-navy hover:bg-sam-navy2" onClick={() => setOpen(true)} data-testid="listing-ai-chat-open"><Bot className="h-4 w-4 mr-2" />Chat with {b.name}'s AI assistant</Button>;
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="listing-ai-chat">
      <div className="font-display font-bold text-sam-navy text-sm flex items-center gap-2 mb-2"><Bot className="h-4 w-4 text-sam-crimson" />{b.name} · AI assistant</div>
      <div className="max-h-64 overflow-auto space-y-2 mb-2" data-testid="listing-ai-chat-log">
        {msgs.length === 0 && <p className="text-xs text-sam-slate">Ask about hours, services or deals.</p>}
        {msgs.map((m, i) => <div key={`${m.role}-${i}`} className={`text-sm rounded-lg px-3 py-2 ${m.role === "user" ? "bg-sam-navy text-white ml-6" : "bg-slate-100 text-sam-navy mr-6"}`}>{m.text.replace(/\*\*|__|`/g, "")}</div>)}
        {busy && <Loader2 className="h-4 w-4 animate-spin text-sam-slate" />}
      </div>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Type a question" data-testid="listing-ai-chat-input" />
        <Button size="sm" onClick={send} disabled={busy} className="bg-sam-crimson" data-testid="listing-ai-chat-send"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
